import { useEffect, useState, useCallback } from "react";
import { Target, Plus, Loader2, CheckCircle2, Trash2, Pencil, Zap, Power } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { VoiceAgentConfig } from "../components/VoiceAgentConfig";

const EMPTY = { nome: "", settore: "", settore_label: "", canale: "", descrizione: "", cliente_tipo: "", tono: "" };

export default function Contesto() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/context"); setData(data); }
    catch (e) { toast.error(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickSettore = (key, label) => setForm((f) => ({ ...f, settore: key, settore_label: label, nome: f.nome || label }));

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (c) => { setForm({ nome: c.nome, settore: c.settore, settore_label: c.settore_label, canale: c.canale || "", descrizione: c.descrizione || "", cliente_tipo: c.cliente_tipo || "", tono: c.tono || "" }); setEditingId(c.id); setShowForm(true); };

  const save = async () => {
    if (!form.settore) { toast.error("Seleziona un settore"); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/context/${editingId}`, form);
      else await api.post("/context", form);
      toast.success(editingId ? "Contesto aggiornato" : "Contesto salvato");
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };
  const activate = async (id) => { try { await api.post(`/context/${id}/activate`); toast.success("Contesto attivato — il suggeritore live lo userà"); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const deactivate = async () => { try { await api.post(`/context/deactivate`); toast.success("Nessun contesto attivo"); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const remove = async (id) => { if (!window.confirm("Eliminare questo contesto?")) return; try { await api.delete(`/context/${id}`); toast.success("Contesto eliminato"); load(); } catch (e) { toast.error(errorMessage(e)); } };

  if (!data) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Caricamento…</div>;

  return (
    <div data-testid="context-page" className="space-y-8">
      <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Impostazioni vendita</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Contesto</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Imposta il tuo settore e come vendi: il <b>contesto attivo</b> viene passato al suggeritore live, al coach e all'agente vocale, così i suggerimenti sono già calibrati sul tuo mercato.</p>
        </div>
        <button data-testid="context-new-btn" onClick={openNew} className="inline-flex items-center gap-2 self-start rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8]"><Plus className="h-4 w-4" /> Nuovo contesto</button>
      </div>

      {data.attivo ? (
        <div data-testid="context-active" className="surface flex flex-col gap-3 border-emerald-500/30 p-5 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "rgba(16,185,129,0.35)" }}>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300"><Zap className="h-5 w-5" /></span>
            <div>
              <p className="text-xs font-mono uppercase tracking-wide text-emerald-300">Contesto attivo</p>
              <p className="text-base font-semibold md:text-lg">{data.attivo.nome} · {data.attivo.settore_label}</p>
              <p className="text-xs text-muted-foreground">{[data.attivo.canale, data.attivo.tono].filter(Boolean).join(" · ") || "—"}</p>
            </div>
          </div>
          <button data-testid="context-deactivate-btn" onClick={deactivate} className="inline-flex items-center gap-2 self-start rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-white"><Power className="h-4 w-4" /> Disattiva</button>
        </div>
      ) : (
        <div data-testid="context-none" className="surface p-5 text-sm text-muted-foreground">Nessun contesto attivo. Il suggeritore live userà solo la knowledge base. Attiva un contesto per calibrare i suggerimenti sul tuo settore.</div>
      )}

      {showForm && (
        <div data-testid="context-form" className="surface space-y-5 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold md:text-lg">{editingId ? "Modifica contesto" : "Nuovo contesto"}</h2>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-sm text-muted-foreground hover:text-white">Chiudi</button>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Settore</label>
            <div data-testid="context-settori" className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {data.settori.map((s) => (
                <button key={s.key} data-testid={`settore-${s.key}`} onClick={() => pickSettore(s.key, s.label)} className={`rounded-xl border px-3 py-3 text-left text-sm transition-all ${form.settore === s.key ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground hover:border-primary/50 hover:text-white"}`}>
                  <Target className="mb-1 h-4 w-4" /> {s.label}
                </button>
              ))}
            </div>
            <input data-testid="settore-custom-input" value={form.settore && !data.settori.some((s) => s.key === form.settore) ? form.settore_label : ""} onChange={(e) => setForm((f) => ({ ...f, settore: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_"), settore_label: e.target.value }))} placeholder="…oppure scrivi un settore personalizzato" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Nome contesto</label>
              <input data-testid="context-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Es. Fotovoltaico PMI" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Canale di vendita</label>
              <select data-testid="context-canale-select" value={form.canale} onChange={(e) => setForm({ ...form, canale: e.target.value })} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                <option value="">—</option>
                {data.canali.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Prodotto / servizio</label>
              <textarea data-testid="context-descrizione-input" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} rows={3} placeholder="Cosa vendi in breve" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cliente-tipo</label>
              <textarea data-testid="context-cliente-input" value={form.cliente_tipo} onChange={(e) => setForm({ ...form, cliente_tipo: e.target.value })} rows={3} placeholder="A chi ti rivolgi" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tono desiderato</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.toni.map((t) => (
                <button key={t} data-testid={`tono-${t}`} onClick={() => setForm({ ...form, tono: form.tono === t ? "" : t })} className={`rounded-full border px-3 py-1.5 text-sm capitalize transition-all ${form.tono === t ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground hover:text-white"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="rounded-full px-4 py-2 text-sm text-muted-foreground hover:text-white">Annulla</button>
            <button data-testid="context-save-btn" onClick={save} disabled={saving || !form.settore || !form.nome.trim()} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50">{saving && <Loader2 className="h-4 w-4 spin-slow" />} Salva</button>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold md:text-lg">Contesti salvati <span className="font-mono text-muted-foreground">({data.items.length})</span></h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {data.items.length === 0 && <p className="text-sm text-muted-foreground">Nessun contesto salvato. Creane uno per iniziare.</p>}
          {data.items.map((c) => (
            <div key={c.id} data-testid={`context-card-${c.id}`} className={`surface flex flex-col gap-3 p-4 ${c.attivo ? "border-emerald-500/40" : ""}`} style={c.attivo ? { borderColor: "rgba(16,185,129,0.4)" } : {}}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">{c.nome}</p>
                  <p className="text-xs text-muted-foreground">{c.settore_label}{c.canale ? ` · ${c.canale}` : ""}{c.tono ? ` · ${c.tono}` : ""}</p>
                </div>
                {c.attivo && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-300"><CheckCircle2 className="h-3 w-3" /> attivo</span>}
              </div>
              {c.descrizione && <p className="text-sm text-gray-300">{c.descrizione}</p>}
              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
                {!c.attivo && <button data-testid={`context-activate-${c.id}`} onClick={() => activate(c.id)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20"><Zap className="h-3.5 w-3.5" /> Attiva</button>}
                <button data-testid={`context-edit-${c.id}`} onClick={() => openEdit(c)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-white"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
                <button data-testid={`context-delete-${c.id}`} onClick={() => remove(c.id)} className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && <VoiceAgentConfig />}
    </div>
  );
}
